import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Building2, 
    Users, 
    TrendingUp, 
    UserPlus,
    ChevronRight,
    LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SuperadminDashboard = () => {
    const { user, token, logout } = useAuth();
    
    const [stats, setStats] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, orgsRes] = await Promise.all([
                axios.get(`${API}/superadmin/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API}/superadmin/organizations`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setStats(statsRes.data);
            setOrganizations(orgsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/superadmin/login';
    };

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Header */}
            <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                            <Building2 size={20} className="text-slate-900" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-white">Superadmin</h1>
                            <p className="text-xs text-slate-400">{user?.email}</p>
                        </div>
                    </div>
                    <Button 
                        variant="ghost" 
                        className="text-slate-300 hover:text-white hover:bg-slate-700"
                        onClick={handleLogout}
                        data-testid="superadmin-logout-btn"
                    >
                        <LogOut size={18} className="mr-2" />
                        Sign Out
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-8 animate-fade-in">
                    {/* Page Title */}
                    <div>
                        <h2 className="text-3xl font-bold text-white">Dashboard</h2>
                        <p className="text-slate-400 mt-1">
                            Global overview of all organizations
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card className="bg-slate-800 border-slate-700" data-testid="total-orgs-stat">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                        <Building2 size={24} className="text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Organizations</p>
                                        <p className="text-2xl font-bold text-white">
                                            {stats?.total_organizations || 0}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-800 border-slate-700" data-testid="total-users-stat">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                                        <Users size={24} className="text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Total Users</p>
                                        <p className="text-2xl font-bold text-white">
                                            {stats?.total_users || 0}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-800 border-slate-700" data-testid="total-referrals-stat">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                                        <UserPlus size={24} className="text-purple-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Total Referrals</p>
                                        <p className="text-2xl font-bold text-white">
                                            {stats?.total_referrals || 0}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-800 border-slate-700" data-testid="conversion-rate-stat">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                        <TrendingUp size={24} className="text-amber-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Conversion Rate</p>
                                        <p className="text-2xl font-bold text-white">
                                            {stats?.conversion_rate || 0}%
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Organizations List */}
                    <Card className="bg-slate-800 border-slate-700" data-testid="organizations-list">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-white">Organizations</CardTitle>
                            <Link to="/superadmin/organizations">
                                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                                    View all <ChevronRight size={16} className="ml-1" />
                                </Button>
                            </Link>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-16 bg-slate-700 rounded-lg animate-pulse" />
                                    ))}
                                </div>
                            ) : organizations.length === 0 ? (
                                <div className="text-center py-12">
                                    <Building2 size={48} className="mx-auto text-slate-600 mb-3" />
                                    <p className="text-slate-400">No organizations yet</p>
                                    <Link to="/superadmin/organizations">
                                        <Button className="mt-4 bg-white text-slate-900 hover:bg-slate-100">
                                            Create Organization
                                        </Button>
                                    </Link>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {organizations.slice(0, 5).map((org) => (
                                        <Link 
                                            key={org.id}
                                            to={`/superadmin/organizations/${org.id}`}
                                            className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                                            data-testid={`org-item-${org.id}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div 
                                                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-medium"
                                                    style={{ backgroundColor: org.primary_color }}
                                                >
                                                    {org.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-white">{org.name}</p>
                                                    <p className="text-sm text-slate-400">/{org.organization_code}</p>
                                                </div>
                                            </div>
                                            <ChevronRight size={20} className="text-slate-500" />
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
};
